import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/shared/lib/supabase';
import { useMyGroups } from '@/features/groups/hooks/useGroups';
import { logger } from '@/shared/lib/logger';
import { buildIndex, type SearchItem } from './searchIndex';
import { Money } from '@shared/money';
import type { CurrencyCode } from '@shared/currencies';

interface ExpenseRow {
  id: string;
  group_id: string;
  merchant_name_raw: string | null;
  amount_cents: number;
  currency: string;
  tags: string[] | null;
  expense_date: string;
}

interface MemberRow {
  participant_id: string;
  group_id: string;
  participant: { display_name: string; email: string | null };
}

/**
 * Trae grupos + hasta N expenses recientes + miembros que el usuario puede ver
 * (RLS filtra automáticamente). Cache generoso (5min) — la palette es best-effort,
 * si el user acaba de crear un gasto y no aparece, no pasa nada.
 */
async function fetchIndexData(): Promise<{ expenses: ExpenseRow[]; members: MemberRow[] }> {
  const [expensesRes, membersRes] = await Promise.all([
    supabase
      .from('expenses')
      .select('id, group_id, merchant_name_raw, amount_cents, currency, tags, expense_date')
      .is('deleted_at', null)
      .order('expense_date', { ascending: false })
      .limit(200),
    supabase
      .from('group_members')
      .select('participant_id, group_id, participant:participants!inner(display_name, email)')
      .is('left_at', null),
  ]);
  if (expensesRes.error) logger.error('search index expenses', { code: expensesRes.error.code });
  if (membersRes.error) logger.error('search index members', { code: membersRes.error.code });
  return {
    expenses: (expensesRes.data ?? []) as ExpenseRow[],
    members: (membersRes.data ?? []) as unknown as MemberRow[],
  };
}

export function useSearchIndex() {
  const { data: groups = [] } = useMyGroups();
  const { data: raw } = useQuery({
    queryKey: ['search', 'index'],
    queryFn: fetchIndexData,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  return useMemo(() => {
    const items: SearchItem[] = [];

    for (const g of groups) {
      items.push({
        kind: 'group',
        id: g.id,
        title: g.name,
        subtitle: g.description ?? undefined,
        url: `/groups/${g.id}`,
      });
    }

    if (raw?.expenses) {
      for (const e of raw.expenses) {
        const title = e.merchant_name_raw ?? '(sin comercio)';
        items.push({
          kind: 'expense',
          id: e.id,
          title,
          // `/100` fijo mostraba los importes en yenes cien veces más pequeños.
          subtitle: `${Money.fromCents(BigInt(Math.trunc(e.amount_cents)), e.currency as CurrencyCode).format()} · ${e.expense_date}`,
          url: `/groups/${e.group_id}/expenses/${e.id}`,
          keywords: e.tags ?? undefined,
        });
      }
      // Tags únicos → item propio, url a la vista de expenses del grupo
      const seenTags = new Set<string>();
      for (const e of raw.expenses) {
        for (const tag of e.tags ?? []) {
          const key = `${e.group_id}::${tag}`;
          if (seenTags.has(key)) continue;
          seenTags.add(key);
          items.push({
            kind: 'tag',
            id: key,
            title: `#${tag}`,
            subtitle: groups.find((g) => g.id === e.group_id)?.name ?? '',
            url: `/groups/${e.group_id}/expenses`,
          });
        }
      }
    }

    if (raw?.members) {
      const seen = new Set<string>();
      for (const m of raw.members) {
        if (seen.has(m.participant_id)) continue;
        seen.add(m.participant_id);
        items.push({
          kind: 'member',
          id: m.participant_id,
          title: m.participant.display_name,
          subtitle: m.participant.email ?? undefined,
          url: `/groups/${m.group_id}`,
          keywords: m.participant.email ? [m.participant.email] : undefined,
        });
      }
    }

    return { items, fuse: buildIndex(items) };
  }, [groups, raw]);
}
